import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {isArray} from 'ramda-adjunct';

import {
  useCancelCashRegisterDocumentMutation,
  useRenderExpenseCashReceiptDocumentMutation,
  useRenderIncomeCashReceiptDocumentMutation,
} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {accountingRoutes, testIds} from '@dms/routes';
import {DmsDataGrid, downloadFile, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['type', 'number'] as const;

export function CashReceiptsOverview() {
  const navigate = useNavigate();
  const [cancelDocument] = useCancelCashRegisterDocumentMutation();
  const [renderIncomeCashRegisterDocument] = useRenderIncomeCashReceiptDocumentMutation();
  const [renderExpenseCashRegisterDocument] = useRenderExpenseCashReceiptDocumentMutation();

  const handleCancelDocument = useCallback(
    (id: string, refreshData: () => void) => {
      const issuedAt = new Date().toISOString();
      cancelDocument({
        cashRegisterDocumentId: id,
        cancelCashRegisterDocumentRequestBody: {issuedAt},
      })
        .unwrap()
        .then(() => refreshData())
        .catch(handleApiError);
    },
    [cancelDocument]
  );

  const actionCallback = useCallback<ActionCallback<typeof PREDEFINED_COLUMNS>>(
    ({actionKey, rowId, getColumnData, refreshData}) => {
      const id = isArray(rowId) ? rowId[0] : rowId;

      const typeKey = getColumnData<{key: string}>('type')?.value?.key;
      const documentNumber = getColumnData<string>('number')?.value;

      const isIncome = typeKey === 'income';

      match(actionKey)
        .with('detail', () =>
          navigate(composePath(accountingRoutes.cashReceiptDetail, {params: {id}}))
        )
        .with('cancel', () =>
          openDeleteDialog({
            onConfirm: () => handleCancelDocument(id, refreshData),
            text: isIncome
              ? i18n.t('entity.cashRegister.notifications.cancelIncomeCashRegisterDocument', {
                  documentNumber,
                })
              : i18n.t('entity.cashRegister.notifications.cancelExpenseCashRegisterDocument', {
                  documentNumber,
                }),
          })
        )
        .with('download', async () => {
          const renderAction = isIncome
            ? renderIncomeCashRegisterDocument({
                renderDocumentRequestBody: {cashRegisterDocumentId: id},
              })
            : renderExpenseCashRegisterDocument({
                renderDocumentRequestBody: {cashRegisterDocumentId: id},
              });

          await renderAction
            .unwrap()
            .then((data) => data.pdfUrl)
            .then(downloadFile)
            .catch(handleApiError);
        })
        .otherwise(showNotification.error);
    },
    [
      handleCancelDocument,
      navigate,
      renderExpenseCashRegisterDocument,
      renderIncomeCashRegisterDocument,
    ]
  );

  return (
    <>
      <Helmet title={i18n.t('page.accounting.invoiceList.title')} />
      <DmsDataGrid
        gridCode="cash-receipts"
        actionCallback={actionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        data-testid={testIds.accounting.cashReceiptsOverview('datagrid')}
      />
    </>
  );
}
